import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type UnitSystem = 'metric' | 'imperial';
export type Species = 'pike' | 'perch' | 'zander' | 'general' | 'trout' | 'salmon' | 'seaTrout';

interface SettingsContextType {
  unitSystem: UnitSystem;
  setUnitSystem: (unit: UnitSystem) => void;
  defaultSpecies: Species;
  setDefaultSpecies: (species: Species) => void;
  defaultMethod: string; // Method key (e.g., 'methodSpin', 'methodTrolling', or custom name)
  setDefaultMethod: (method: string) => void;
  defaultTackle: string[]; // Array of tackle keys (multi-select)
  setDefaultTackle: (tackle: string[]) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [unitSystem, setUnitSystemState] = useState<UnitSystem>('metric');
  const [defaultSpecies, setDefaultSpeciesState] = useState<Species>('pike');
  const [defaultMethod, setDefaultMethodState] = useState<string>('methodSpin');
  const [defaultTackle, setDefaultTackleState] = useState<string[]>([]);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedUnit = localStorage.getItem('unitSystem') as UnitSystem;
    const savedSpecies = localStorage.getItem('defaultSpecies') as Species;
    const savedMethod = localStorage.getItem('defaultMethod');
    const savedTackle = localStorage.getItem('defaultTackle');
    
    if (savedUnit) {
      setUnitSystemState(savedUnit);
    }
    if (savedSpecies) {
      setDefaultSpeciesState(savedSpecies);
    }
    if (savedMethod) {
      setDefaultMethodState(savedMethod);
    }
    if (savedTackle) {
      try {
        setDefaultTackleState(JSON.parse(savedTackle));
      } catch (e) {
        console.error('Failed to parse defaultTackle:', e);
      }
    }
  }, []);

  const setUnitSystem = (unit: UnitSystem) => {
    setUnitSystemState(unit);
    localStorage.setItem('unitSystem', unit);
  };

  const setDefaultSpecies = (species: Species) => {
    setDefaultSpeciesState(species);
    localStorage.setItem('defaultSpecies', species);
  };

  const setDefaultMethod = (method: string) => {
    setDefaultMethodState(method);
    localStorage.setItem('defaultMethod', method);
  };

  const setDefaultTackle = (tackle: string[]) => {
    setDefaultTackleState(tackle);
    localStorage.setItem('defaultTackle', JSON.stringify(tackle));
  };

  return (
    <SettingsContext.Provider
      value={{
        unitSystem,
        setUnitSystem,
        defaultSpecies,
        setDefaultSpecies,
        defaultMethod,
        setDefaultMethod,
        defaultTackle,
        setDefaultTackle,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
